import React, { useEffect, useRef } from 'react'
import gsap from 'gsap'

const BASE = 'ice-assets/basket-bonus'

// spriteBalloon{Colour}.png is a 4-frame vertical strip, and the birds in it are
// the same three the scene uses, already drawn sitting in the weave:
//   0 = empty basket, 1 = motu, 2 = patlu, 3 = the bow-tie one (spritePenguin)
export const BALLOON_FRAMES = 4
export const BALLOON_W = 212
export const BALLOON_H = 260

// Where the basket sits inside a frame, scanned off the sprite: above y 182 of
// 259 there are only the two ropes, and the weave runs from there to the foot.
// x 33..172 of 212 gives its centre.

// The basket does not go straight up — it swings side to side the whole way.
// Measured off gamevideo.mp4 on 2026-08-14, tracking single units across the
// scenery (the clip is a phone pointed at a monitor, so each frame pair was
// locally registered on the background to take the camera out, and everything
// was then scaled by the sprite's own on-screen size — the two balloon centres
// are 136.6px apart in a 212px-wide frame — because the monitor is at an angle
// and no single px->% factor holds across the picture):
//
//   red unit  17.3-22.3s   troughs 17.60s / 20.60s, peak 19.10s
//   green unit 6.6-9.0s    peak 6.97s, trough 8.53s
//
// so a half-pass is 1.50, 1.50 and 1.57s — SWING_HALF is that, and the full
// period is ~3.0s.
//
// Re-measured 2026-09-17 on the clean capture (youtube n0wAAQk3gPo, 2:40-3:04),
// which settles what the phone clip could not:
//   - the swing is a TRIANGLE, not a sine: the purple unit steps a flat ~1.1% of
//     the stage per 0.2s and turns round inside one sample (67.2 67.2 66.1 64.7
//     63.9 62.5 61.4 60.3 59.3 | 60.6 61.8 ...). Peaks 172.1 / 173.6 / 175.0 /
//     176.6 / 178.1 / 179.6s — 1.5s a half, again.
//   - peak to peak is 7.2-7.9% of the stage against a 20%-wide unit: SWAY.
//   - every unit swings off ONE clock. The blue and purple units, spawned 6s
//     apart, hit their right ends on the same frame (172.0) and their left ends
//     on the same frame (173.6); the unit in the right-hand lane at the same
//     moment was at its LEFT end (80.8% at 172.0 and 175.0, 88% at 170.5 and
//     173.5). So the two lanes swing mirror-wise and every unit in a lane stays
//     in step with the others — which is why two neighbours never knock into
//     each other. `side` is that mirror, and the clock is gsap's global time, so
//     it stops with the page like every other tween does.
//   - it rises at a steady 10% of the stage height a second (84 -> 25% in 6.0s
//     blue, 5.8s purple), the same for every unit — nothing overtakes.
//     gubbare-ani-video.mp4 agrees: over 14.0-16.5s, with the camera registered
//     out on the ice at the left, the red unit climbs ~45px/s on a screen ~424px
//     tall — 10.6%. So a balloon takes ~10s to cross the screen: RISE_SPEED, in
//     STAGE heights a second. It was once tied to the unit's own height instead,
//     and because our unit is taller on the stage than the capture's (~44% of a
//     16:9 viewport against 31%) that ran the sky ~40% faster than the video.
//
// It does NOT lean. Over the well-conditioned stretch of the red unit (52
// frames, both balloons fully visible) the two balloons stay level to within
// ±0.8°, where a 5° lean would have shown as a ~10px height difference at that
// gap; the same is plain on a tracked crop sheet, where the basket rim stays
// horizontal the whole way up. So there is one element and no rotation — which
// also leaves BasketBonus's aim reading the sprite's true upright rect.
const SWING_HALF = 1.5
const SWAY = 0.375
export const RISE_SPEED = 0.10

// -1..1..-1 over two half-passes, straight lines with a hard turn at each end
const triangle = (u) => {
    const p = ((u % 2) + 2) % 2
    return p < 1 ? 2 * p - 1 : 3 - 2 * p
}

/**
 * One balloon + basket that drifts up the scene.
 * `frame` 0 = empty, 1..3 = carrying a penguin (set to the landed bird's own
 * cell when the player puts one in).
 * `side` is 1 for the centre lane and -1 for the right one: the two lanes swing
 * as mirror images of each other.
 */
function Balloon({ colour, xPct, side = 1, frame = 0, prize = null, onDone, registerRef }) {
    const ref = useRef(null)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        registerRef && registerRef(el)

        // Rise from just below the stage to just above it. The distance has to be
        // measured in px off the stage — a % on `y` resolves against the balloon's
        // own height, which only carries it a fraction of the way up.
        const stageH = el.parentElement ? el.parentElement.clientHeight : 0
        const elH = el.offsetHeight

        // the run is stage + 2.1 unit heights (see the fromTo below), at a fixed
        // pace in stage heights — so every basket climbs at the same speed
        const rise = stageH > 0 ? (stageH + elH * 2.1) / (stageH * RISE_SPEED) : 19

        // the swing itself — across the spawn column, not off to one side of it,
        // read off the shared clock so the unit joins its lane in step
        const throwPx = el.offsetWidth * SWAY
        const swingX = () => side * (throwPx / 2) * triangle(gsap.globalTimeline.time() / SWING_HALF)

        const tl = gsap.timeline({ onComplete: () => onDone && onDone() })
        tl.fromTo(el,
            { y: elH * 1.1, x: swingX(), opacity: 0 },
            { opacity: 1, duration: 0.4, ease: 'power1.out' }, 0)
        tl.to(el, {
            y: -(stageH + elH),
            duration: rise,
            ease: 'none',
            onUpdate: () => gsap.set(el, { x: swingX() }),
        }, 0)

        return () => tl.kill()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div
            ref={ref}
            className='bk-balloon'
            style={{
                left: `${xPct}%`,
                backgroundImage: `url(${BASE}/sprites/spriteBalloon${colour}.png)`,
                backgroundSize: `100% ${BALLOON_FRAMES * 100}%`,
                backgroundPositionY: `${(frame * 100) / (BALLOON_FRAMES - 1)}%`,
                aspectRatio: `${BALLOON_W} / ${BALLOON_H}`,
            }}
        >
            {prize != null && <span className='bk-balloon-prize'>{prize}</span>}
        </div>
    )
}

export default Balloon
